import { createContext, useContext, useEffect, useState } from "react";
import { STRINGS, RTL } from "./langs";

const SHARED = {
    id: { s17: "Bagikan hasil", s18: "Salin tautan", s19: "Tersalin" },
    en: { s17: "Share result", s18: "Copy link", s19: "Copied" },
    jv: { s17: "Bagikan asil", s18: "Salin pranala", s19: "Kesalin" },
    su: { s17: "Bagikeun hasil", s18: "Salin tautan", s19: "Kasalin" },
    ms: { s17: "Kongsi hasil", s18: "Salin pautan", s19: "Disalin" },
    tl: { s17: "Ibahagi ang resulta", s18: "Kopyahin ang link", s19: "Nakopya" },
    th: { s17: "แชร์ผลลัพธ์", s18: "คัดลอกลิงก์", s19: "คัดลอกแล้ว" },
    vi: { s17: "Chia sẻ kết quả", s18: "Sao chép liên kết", s19: "Đã sao chép" },
    zh: { s17: "分享结果", s18: "复制链接", s19: "已复制" },
    ja: { s17: "結果を共有", s18: "リンクをコピー", s19: "コピー済み" },
    ko: { s17: "결과 공유", s18: "링크 복사", s19: "복사됨" },
    hi: { s17: "परिणाम साझा करें", s18: "लिंक कॉपी करें", s19: "कॉपी हो गया" },
    bn: { s17: "ফলাফল শেয়ার করুন", s18: "লিংক কপি করুন", s19: "কপি হয়েছে" },
    ur: { s17: "نتیجہ شیئر کریں", s18: "لنک کاپی کریں", s19: "کاپی ہو گیا" },
    ar: { s17: "شارك النتيجة", s18: "انسخ الرابط", s19: "تم النسخ" },
    tr: { s17: "Sonucu paylaş", s18: "Bağlantıyı kopyala", s19: "Kopyalandı" },
    ru: { s17: "Поделиться результатом", s18: "Копировать ссылку", s19: "Скопировано" },
    es: { s17: "Compartir resultado", s18: "Copiar enlace", s19: "Copiado" },
    pt: { s17: "Compartilhar resultado", s18: "Copiar link", s19: "Copiado" },
    fr: { s17: "Partager le résultat", s18: "Copier le lien", s19: "Copié" },
    de: { s17: "Ergebnis teilen", s18: "Link kopieren", s19: "Kopiert" },
    nl: { s17: "Deel resultaat", s18: "Kopieer link", s19: "Gekopieerd" },
    it: { s17: "Condividi risultato", s18: "Copia link", s19: "Copiato" },
    sw: { s17: "Shiriki matokeo", s18: "Nakili kiungo", s19: "Imenakiliwa" },
};

const I18nCtx = createContext(null);

export function I18nProvider({ children }) {
    const [lang, setLang] = useState(() => localStorage.getItem("refundaja-lang") || "id");
    useEffect(() => {
        localStorage.setItem("refundaja-lang", lang);
        document.documentElement.lang = lang;
        document.documentElement.dir = RTL.includes(lang) ? "rtl" : "ltr";
    }, [lang]);
    const t = { ...(STRINGS[lang] || STRINGS.id), ...(SHARED[lang] || SHARED.id) };
    const locale = t._loc || lang;
    return <I18nCtx.Provider value={{ lang, setLang, t, locale }}>{children}</I18nCtx.Provider>;
}

export const useI18n = () => useContext(I18nCtx);

export const fmtDate = (locale, d, opts) => {
    try {
        return new Intl.DateTimeFormat(locale, opts).format(d);
    } catch {
        return new Intl.DateTimeFormat("id-ID", opts).format(d);
    }
};

export const RH_LABELS = {
    id: { rh1: "Riwayat", rh2: "Hapus semua", rh3: "Muat", rh4: "Belum ada riwayat — hasil perhitunganmu tersimpan otomatis di sini.", rh5: "Yakin?" },
    en: { rh1: "History", rh2: "Clear all", rh3: "Load", rh4: "No history yet — your results are saved here automatically.", rh5: "Sure?" },
    jv: { rh1: "Riwayat", rh2: "Busek kabeh", rh3: "Buka", rh4: "Durung ana riwayat — asile kasimpen otomatis nang kene.", rh5: "Yakin?" },
    su: { rh1: "Riwayat", rh2: "Pupuseun sadaya", rh3: "Buka", rh4: "Can aya riwayat — hasil kasimpen otomatis di dieu.", rh5: "Yakin?" },
    ms: { rh1: "Sejarah", rh2: "Padam semua", rh3: "Muat", rh4: "Belum ada sejarah — hasil anda disimpan automatik di sini.", rh5: "Pasti?" },
    tl: { rh1: "Kasaysayan", rh2: "Burahin lahat", rh3: "I-load", rh4: "Wala pang kasaysayan — awtomatikong nai-save dito ang mga resulta.", rh5: "Sigurado?" },
    th: { rh1: "ประวัติ", rh2: "ล้างทั้งหมด", rh3: "โหลด", rh4: "ยังไม่มีประวัติ — ผลลัพธ์จะบันทึกอัตโนมัติที่นี่", rh5: "แน่ใจ?" },
    vi: { rh1: "Lịch sử", rh2: "Xóa tất cả", rh3: "Tải lại", rh4: "Chưa có lịch sử — kết quả của bạn được lưu tự động tại đây.", rh5: "Chắc chắn?" },
    zh: { rh1: "历史记录", rh2: "全部清除", rh3: "载入", rh4: "暂无历史记录——您的计算结果会自动保存在这里。", rh5: "确定？" },
    ja: { rh1: "履歴", rh2: "すべて削除", rh3: "読み込む", rh4: "履歴はまだありません——計算結果はここに自動保存されます。", rh5: "本当に？" },
    ko: { rh1: "기록", rh2: "모두 삭제", rh3: "불러오기", rh4: "기록이 없습니다 — 계산 결과가 여기에 자동 저장됩니다.", rh5: "확실해요?" },
    hi: { rh1: "इतिहास", rh2: "सब हटाएं", rh3: "लोड करें", rh4: "अभी कोई इतिहास नहीं — आपके परिणाम यहाँ अपने आप सहेजे जाते हैं।", rh5: "पक्का?" },
    bn: { rh1: "ইতিহাস", rh2: "সব মুছুন", rh3: "লোড", rh4: "এখনও কোনো ইতিহাস নেই — আপনার ফলাফল এখানে স্বয়ংক্রিয়ভাবে সংরক্ষিত হয়।", rh5: "নিশ্চিত?" },
    ur: { rh1: "تاریخ", rh2: "سب حذف کریں", rh3: "لوڈ", rh4: "ابھی کوئی تاریخ نہیں — آپ کے نتائج یہاں خودکار محفوظ ہوتے ہیں۔", rh5: "پکا؟" },
    ar: { rh1: "السجل", rh2: "مسح الكل", rh3: "تحميل", rh4: "لا يوجد سجل بعد — تُحفَظ نتائجك هنا تلقائياً.", rh5: "متأكد؟" },
    tr: { rh1: "Geçmiş", rh2: "Tümünü sil", rh3: "Yükle", rh4: "Henüz geçmiş yok — sonuçların burada otomatik kaydedilir.", rh5: "Emin misin?" },
    ru: { rh1: "История", rh2: "Очистить всё", rh3: "Загрузить", rh4: "Истории пока нет — результаты сохраняются здесь автоматически.", rh5: "Точно?" },
    es: { rh1: "Historial", rh2: "Borrar todo", rh3: "Cargar", rh4: "Sin historial aún — tus resultados se guardan aquí automáticamente.", rh5: "¿Seguro?" },
    pt: { rh1: "Histórico", rh2: "Apagar tudo", rh3: "Carregar", rh4: "Sem histórico ainda — seus resultados são salvos aqui automaticamente.", rh5: "Tem certeza?" },
    fr: { rh1: "Historique", rh2: "Tout effacer", rh3: "Charger", rh4: "Pas encore d'historique — vos résultats s'enregistrent ici automatiquement.", rh5: "Sûr ?" },
    de: { rh1: "Verlauf", rh2: "Alle löschen", rh3: "Laden", rh4: "Noch kein Verlauf — deine Ergebnisse werden hier automatisch gespeichert.", rh5: "Sicher?" },
    nl: { rh1: "Geschiedenis", rh2: "Alles wissen", rh3: "Laden", rh4: "Nog geen geschiedenis — je resultaten worden hier automatisch opgeslagen.", rh5: "Zeker?" },
    it: { rh1: "Cronologia", rh2: "Cancella tutto", rh3: "Carica", rh4: "Nessuna cronologia — i tuoi risultati si salvano qui automaticamente.", rh5: "Sicuro?" },
    sw: { rh1: "Historia", rh2: "Futa zote", rh3: "Pakia", rh4: "Hakuna historia bado — matokeo yako huhifadhiwa hapa moja kwa moja.", rh5: "Una uhakika?" },
};
